// ✅ src/hooks/usePrayerStore.ts (final stable version)
import { create } from 'zustand';
import { Prayer } from '@/types/prayer';
import { PrayerService } from '@/services/PrayerService';
import { supabase } from '@/integrations/supabase/client';

interface PrayerStoreState {
  userPrayers: Prayer[];
  communityPrayers: Prayer[];
  favoritePrayers: Prayer[];
  bookmarkedPrayers: Prayer[];
  isLoading: boolean;
  error: string | null;

  // Core setters
  setUserPrayers: (prayers: Prayer[] | ((prev: Prayer[]) => Prayer[])) => void;
  setCommunityPrayers: (prayers: Prayer[] | ((prev: Prayer[]) => Prayer[])) => void;

  // Data operations
  refreshPrayers: () => Promise<void>;
  addPrayer: (prayer: Prayer) => Promise<Prayer>;
  updatePrayer: (id: string, updates: Partial<Prayer>) => Promise<boolean>;
  deletePrayer: (id: string) => Promise<boolean>;
}

// --- Utility mapper ---
function normalizePrayer(row: any): Prayer {
  return {
    id: row.id,
    title: row.title || 'Untitled Prayer',
    content: row.content || '',
    author:
      row.author_name ||
      row.profiles?.full_name ||
      row.profiles?.username ||
      'Anonymous',
    author_id: row.author_id,
    date: row.created_at || new Date().toISOString(),
    category: row.category || 'general',
    status: row.status || 'active',
    visibility: row.visibility || 'public',
    group: row.circle_id || row.group || null,
    isFavorite: !!row.isFavorite,
    isBookmarked: !!row.isBookmarked,
    audioStoragePath: row.audioStoragePath,
    authorAvatarStoragePath: row.authorAvatarStoragePath,
    testimony: row.testimony || undefined,
    isTestimonyPublic: !!row.is_testimony_public,
  };
}

// --- Internal guard to prevent redundant reload ---
let lastActionRef = { table: '', ts: 0 };

export const usePrayerStore = create<PrayerStoreState>((set, get) => ({
  userPrayers: [],
  communityPrayers: [],
  favoritePrayers: [],
  bookmarkedPrayers: [],
  isLoading: false,
  error: null,

  setUserPrayers: (prayers) =>
    set((state) => ({
      userPrayers:
        typeof prayers === 'function' ? prayers(state.userPrayers) : prayers,
    })),

  setCommunityPrayers: (prayers) =>
    set((state) => ({
      communityPrayers:
        typeof prayers === 'function'
          ? prayers(state.communityPrayers)
          : prayers,
    })),

  refreshPrayers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user || null;

      const { data: communityData, error: communityError } =
        await PrayerService.listAllEnriched(user?.id);
      if (communityError) throw communityError;

      let userPrayers: Prayer[] = [];
      let communityPrayers: Prayer[] = [];

      if (user) {
        const { data: userData, error: userError } = await PrayerService.listByAuthor(
          user.id
        );
        if (userError) throw userError;

        const filteredCommunity = (communityData || []).filter(
          (p) => p.author_id !== user.id && p.visibility === 'public'
        );

        userPrayers = (userData || []).map(normalizePrayer);
        communityPrayers = filteredCommunity.map(normalizePrayer);

        // Engagement (favorites/bookmarks)
        const { data: favs } = await supabase
          .from('prayer_favorites')
          .select('prayer_id')
          .eq('user_id', user.id);

        const { data: bookmarks } = await supabase
          .from('prayer_bookmarks')
          .select('prayer_id')
          .eq('user_id', user.id);

        const favoriteIds = new Set(favs?.map((f) => f.prayer_id) || []);
        const bookmarkIds = new Set(bookmarks?.map((b) => b.prayer_id) || []);

        const enrich = (list: Prayer[]) =>
          list.map((p) => ({
            ...p,
            isFavorite: favoriteIds.has(p.id),
            isBookmarked: bookmarkIds.has(p.id),
          }));

        userPrayers = enrich(userPrayers);
        communityPrayers = enrich(communityPrayers);

        set({
          userPrayers,
          communityPrayers,
          favoritePrayers: userPrayers
            .concat(communityPrayers)
            .filter((p) => p.isFavorite),
          bookmarkedPrayers: userPrayers
            .concat(communityPrayers)
            .filter((p) => p.isBookmarked),
        });
      } else {
        const publicPrayers = (communityData || [])
          .filter((p) => p.visibility === 'public')
          .map(normalizePrayer);
        set({ communityPrayers: publicPrayers });
      }

      set({ isLoading: false });
    } catch (err: any) {
      console.error('❌ Error refreshing prayers:', err);
      set({ error: err.message, isLoading: false });
    }
  },

  addPrayer: async (prayer) => {
    const prayerWithMeta = {
      ...prayer,
      id: prayer.id || crypto.randomUUID(),
      date: prayer.date || new Date().toISOString(),
    };
    set((state) => ({
      userPrayers: [prayerWithMeta, ...state.userPrayers],
    }));
    return prayerWithMeta;
  },

  updatePrayer: async (id, updates) => {
    try {
      set((state) => ({
        userPrayers: state.userPrayers.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        ),
        communityPrayers: state.communityPrayers.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        ),
      }));
      return true;
    } catch (error) {
      console.error('⚠️ Update prayer error:', error);
      return false;
    }
  },

  deletePrayer: async (id) => {
    try {
      const { error } = await PrayerService.remove(id);
      if (error) throw error;
      set((state) => ({
        userPrayers: state.userPrayers.filter((p) => p.id !== id),
        communityPrayers: state.communityPrayers.filter((p) => p.id !== id),
      }));
      return true;
    } catch (error) {
      console.error('⚠️ Delete prayer error:', error);
      return false;
    }
  },
}));

// --- Real-time sync (smart, skip self-trigger) ---
const channel = supabase.channel('prayer-realtime-sync');

function shouldSkipReload(table: string) {
  const now = Date.now();
  const diff = now - lastActionRef.ts;
  if (lastActionRef.table === table && diff < 1200) return true; // skip same-table self events
  return false;
}

['prayer_favorites', 'prayer_bookmarks'].forEach((table) => {
  channel.on(
    'postgres_changes',
    { event: '*', schema: 'public', table },
    async (payload) => {
      if (shouldSkipReload(table)) return;
      console.log(`🔄 Realtime sync triggered for ${table}`);
      const { refreshPrayers } = usePrayerStore.getState();
      lastActionRef = { table, ts: Date.now() };
      await refreshPrayers();
    }
  );
});

channel.subscribe();
